import React, { useState, useCallback, useEffect, useRef } from 'react';
import Header from '../components/header';
import DefaultFooter from '../components/default-footer';
import AOS from "aos";
import "aos/dist/aos.css";
import DatePicker, { DateObject } from 'react-multi-date-picker';
import isTextMatched from '../utils/isTextMatched';
import Slider from "react-slick";
import { destinations10 } from '../data/desinations';
import { testimonial1 } from '../data/testimonialData';
import activityData from "./../data/activity";
import { useTranslation } from "react-i18next";
import PromoModal from '../components/promo-modal';
import { useSeo } from "../components/useSeo";
import { organizationJsonLd } from "../components/seoData";

const API_BASE_URL = "https://azure-rabbit-476548.hostingersite.com/public/api";



// ============================================================
// HERO — original carousel version, kept as-is structurally
// ============================================================
const TRUST_STATS = [
  { value: "14+", fallback: "Years serving travelers across Oman" },
  { value: "26,000+", fallback: "Travelers served" },
  { value: "98%", fallback: "Visa approval rate" },
  { value: "40+", fallback: "Destinations covered" },
];

const HERO_SLIDES = [
  {
    img: "./../img/hero_section_1.png",
    pos: "center 38%",
    labelKey: "heroLabel1",
    headlineKeys: ["heroHeadline1_1", "heroHeadline1_2", "heroHeadline1_3"],
    subKey: "heroSub1",
    ctaKey: "heroCta1",
    ctaFallback: "Explore Oman",
    href: "/tour-list",
    align: "left",
  },
  {
    img: "./../img/hero_section_2.png",
    pos: "center 38%",
    labelKey: "heroLabel2",
    headlineKeys: ["heroHeadline2_1", "heroHeadline2_2", "heroHeadline2_3"],
    subKey: "heroSub2",
    ctaKey: "heroCta2",
    ctaFallback: "Discover Experiences",
    href: "/tour-list",
    align: "center",
  },
  {
    img: "./../img/about_us_bg.png",
    pos: "center top",
    labelKey: "heroLabel3",
    headlineKeys: ["heroHeadline3_1", "heroHeadline3_2", "heroHeadline3_3"],
    subKey: "heroSub3",
    ctaKey: "heroCta3",
    ctaFallback: "Plan Your Trip",
    href: "/tour-list",
    align: "right",
  },
  {
    img: "./../img/hero_section_4.png",
    pos: "center 20%",
    labelKey: "heroLabel3",
    headlineKeys: ["heroHeadline3_1", "heroHeadline3_2", "heroHeadline3_3"],
    subKey: "heroSub3",
    ctaKey: "heroCta3",
    ctaFallback: "Plan Your Trip",
    href: "/tour-list",
    align: "right",
  },
];

if (typeof window !== "undefined") {
  HERO_SLIDES.forEach(s => { const i = new Image(); i.src = s.img; });
}

const INTERVAL = 5500;

function HeroSection() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [animKey, setAnimKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((idx: number) => {
    setPrev(active);
    setActive(idx);
    setAnimKey(k => k + 1);
  }, [active]);

  const next = useCallback(() => {
    goTo((active + 1) % HERO_SLIDES.length);
  }, [active, goTo]);

  useEffect(() => {
    timerRef.current = setInterval(next, INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [next]);

  const slide = HERO_SLIDES[active];
  const justify = slide.align === "center" ? "center" : slide.align === "right" ? "flex-end" : "flex-start";
  const textAlign = slide.align === "center" ? "center" : slide.align === "right" ? "right" : "left";

  return (
    <>
      <style>{`
        .hero-root {
          --gold: #d3be76;
          --gold-d: #b09a50;
          --teal-d: #1a3140;
          position: relative;
          height: 78vh;
          min-height: 560px;
          max-height: 820px;
          overflow: hidden;
          background: #0e1e26;
          border-radius: 0 0 28px 28px;
        }

        .hero-bg {
           position: absolute;
           inset: 0;
           width: 100%;
  height: 100%;
          background-size: cover;
          transition: opacity 1s ease-in-out;
           will-change: opacity, transform;
            animation: heroDrift 9s ease-in-out forwards;
         }
        .hero-bg.is-active { opacity: 1; }
        .hero-bg.is-prev { opacity: 0; }
        .hero-bg.is-hidden { opacity: 0; animation: none; }
        @keyframes heroDrift {
          0%   { transform: scale(1.05); }
          100% { transform: scale(1.13); }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-bg { animation: none !important; }
        }

        .hero-veil {
          position: absolute;
          inset: 0;
          z-index: 2;
          pointer-events: none;
          background:
            linear-gradient(90deg, rgba(14,30,38,.88) 0%, rgba(14,30,38,.42) 48%, rgba(14,30,38,.12) 100%),
            linear-gradient(0deg, rgba(14,30,38,.6) 0%, rgba(14,30,38,0) 30%);
        }

        .hero-badge {
          position: absolute;
          left: 40px;
          bottom: 34px;
          z-index: 12;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255,255,255,.08);
          backdrop-filter: blur(6px);
          border: 1px solid rgba(211,190,118,.35);
          border-radius: 999px;
          padding: 8px 16px 8px 10px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: .4px;
          color: #fff;
        }
        .hero-badge svg { color: var(--gold); flex-shrink: 0; }
        @media (max-width: 700px) { .hero-badge { left: 20px; bottom: 74px; } }

        .hero-body {
          position: relative;
          height: 100%;
          z-index: 8;
          display: flex;
          align-items: center;
          padding-bottom: 40px;
        }

        .hero-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: var(--gold);
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }
        .hero-label svg { flex-shrink: 0; }

        .hero-h1 {
          font-weight: 600;
          color: #fff;
          line-height: 1.14;
          font-size: clamp(34px, 4vw, 58px);
          max-width: 680px;
          margin-bottom: 18px;
          text-shadow: 0 2px 24px rgba(0,0,0,.3);
        }
        .hero-h1 span.gold {
          font-style: italic;
          color: var(--gold);
          font-weight: 400;
        }

        .hero-sub {
          font-size: clamp(13px, 1.1vw, 15.5px);
          color: rgba(255, 255, 255, 0.85);
          font-weight: 300;
          line-height: 1.7;
          max-width: 470px;
          margin-bottom: 30px;
        }

        .hero-ctas { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }

        .hbtn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--gold);
          color: var(--teal-d);
          border-radius: 5px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: .3px;
          padding: 15px 28px;
          text-decoration: none;
          transition: transform .2s, background .2s, box-shadow .2s;
          box-shadow: 0 10px 28px rgba(211,190,118,.3);
        }
        .hbtn-primary:hover { background: #e2ce86; transform: translateY(-2px); color: var(--teal-d); }

        .hbtn-ghost {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255,255,255,.1);
          backdrop-filter: blur(4px);
          color: #fff;
          border: 1px solid rgba(255,255,255,.28);
          border-radius: 5px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: .3px;
          padding: 15px 26px;
          text-decoration: none;
          transition: background .2s, border-color .2s;
        }
        .hbtn-ghost:hover { border-color: #fff; background: rgba(255,255,255,.2); color: #fff; }

        .hero-pills {
          position: absolute;
          bottom: 34px;
          right: 40px;
          z-index: 12;
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .hero-pill {
          height: 6px;
          width: 6px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          background: rgba(255, 255, 255, 0.35);
          transition: background .3s, width .3s;
          padding: 0;
        }
        .hero-pill.on { background: var(--gold); width: 22px; border-radius: 3px; }
        @media (max-width: 700px) { .hero-pills { right: 20px; } }
      `}</style>

      <section className="hero-root">
        {HERO_SLIDES.map((s, i) => {
  const cls = i === active ? "hero-bg is-active" : i === prev ? "hero-bg is-prev" : "hero-bg is-hidden";
  return (
    <div
      key={`${i}-${i === active ? animKey : "static"}`}
      className={cls}
      style={{ backgroundImage: `url(${s.img})`, backgroundPosition: s.pos }}
    />
  );
})}

        <div className="hero-veil" />

        <div className="hero-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {t("heroLocationBadge", "Based in Muscat, Sultanate of Oman")}
        </div>

        <div className="hero-body" key={animKey}>
          <div className="container" style={{ display: "flex", justifyContent: justify }}>
            <div style={{ textAlign: textAlign as any, maxWidth: 680 }}>
              <div className="hero-label" style={{ justifyContent: slide.align === "right" ? "flex-end" : "flex-start" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2 L14.5 9.5 L22 12 L14.5 14.5 L12 22 L9.5 14.5 L2 12 L9.5 9.5 Z" />
                </svg>
                {t(slide.labelKey)}
              </div>
              <h1 className="hero-h1">
                {slide.headlineKeys.map((key, j) => (
                  <React.Fragment key={j}>
                    {j === 1 ? <span className="gold">{t(key)} </span> : `${t(key)} `}
                  </React.Fragment>
                ))}
              </h1>
              <p className="hero-sub" style={{ marginLeft: slide.align === "right" ? "auto" : slide.align === "center" ? "auto" : 0, marginRight: slide.align === "left" ? "auto" : slide.align === "center" ? "auto" : 0 }}>
                {t(slide.subKey)}
              </p>
              <div className="hero-ctas" style={{ justifyContent: justify }}>
                <a href={slide.href} className="hbtn-primary">
                  {t(slide.ctaKey, slide.ctaFallback)}
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </a>
                <a href="/contact" className="hbtn-ghost">{t("contactUs")}</a>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-pills">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              className={`hero-pill${active === i ? " on" : ""}`}
              onClick={() => {
                if (timerRef.current) clearInterval(timerRef.current);
                goTo(i);
                timerRef.current = setInterval(next, INTERVAL);
              }}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>
    </>
  );
}


const SERVICE_CATEGORIES = [
  {
    labelKey: "catBusiness",
    fallback: "Business",
    color: "#294b5a",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ),
  },
  {
    labelKey: "catFlights",
    fallback: "Flights",
    color: "#3b7fb0",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2.5 1.8V22l4-1 4 1v-1.2L13 19v-5.5z" />
      </svg>
    ),
  },
  {
    labelKey: "catAirportServices",
    fallback: "Airport Services",
    color: "#b0763b",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="7" width="16" height="13" rx="2" /><path d="M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
      </svg>
    ),
  },
  {
    labelKey: "catVisa",
    fallback: "Visa",
    color: "#7a5cb0",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2" /><circle cx="8" cy="12" r="2" /><line x1="14" y1="9" x2="19" y2="9" /><line x1="14" y1="15" x2="19" y2="15" />
      </svg>
    ),
  },
  {
    labelKey: "catHolidays",
    fallback: "Holidays",
    color: "#c9a227",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.2" y1="4.2" x2="5.6" y2="5.6" /><line x1="18.4" y1="18.4" x2="19.8" y2="19.8" />
      </svg>
    ),
  },
  {
    labelKey: "catRail",
    fallback: "Rail",
    color: "#4a8f6b",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="3" width="16" height="14" rx="2" /><line x1="4" y1="11" x2="20" y2="11" /><circle cx="8.5" cy="17.5" r="1.5" /><circle cx="15.5" cy="17.5" r="1.5" />
      </svg>
    ),
  },
  {
    labelKey: "catCruise",
    fallback: "Cruise",
    color: "#2f96a8",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 18c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0" /><path d="M5 18V9h14v9" /><path d="M9 9V5h6v4" />
      </svg>
    ),
  },
  {
    labelKey: "catStays",
    fallback: "Stays",
    color: "#b05c8a",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M2 20h20" /><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" />
      </svg>
    ),
  },
  {
    labelKey: "catGround",
    fallback: "Ground",
    color: "#5c7ab0",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 17h14l-1.5-6h-11z" /><circle cx="7.5" cy="17" r="1.5" /><circle cx="16.5" cy="17" r="1.5" />
      </svg>
    ),
  },
  {
    labelKey: "catPilgrimage",
    fallback: "Pilgrimage",
    color: "#8a8a3b",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2 L20 20 H4 Z" /><path d="M9 20v-6h6v6" />
      </svg>
    ),
  },
];

function ServiceCategoriesStrip() {
  const { t } = useTranslation();
  const loop = [...SERVICE_CATEGORIES, ...SERVICE_CATEGORIES];

  return (
    <>
      <style>{`
        .cat-strip-wrap {
          background: var(--cream);
          padding: 22px 0;
          overflow: hidden;
          border-bottom: 1px solid rgba(41,75,90,.06);
        }
        .cat-strip-track {
          display: flex;
          width: max-content;
          gap: 14px;
          animation: catScroll 32s linear infinite;
        }
        .cat-strip-wrap:hover .cat-strip-track {
          animation-play-state: paused;
        }
        @keyframes catScroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .cat-pill {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          gap: 9px;
          background: #fff;
          border: 1px solid rgba(41,75,90,.1);
          border-radius: 999px;
          padding: 10px 20px;
          font-size: 13.5px;
          font-weight: 600;
          color: var(--ink);
          white-space: nowrap;
          box-shadow: var(--shadow-sm);
        }
        .cat-pill-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @media (prefers-reduced-motion: reduce) {
          .cat-strip-track { animation: none; }
        }
      `}</style>
      <div className="cat-strip-wrap">
        <div className="cat-strip-track">
          {loop.map((cat, i) => (
            <div className="cat-pill" key={i}>
              <span className="cat-pill-icon" style={{ color: cat.color }}>{cat.icon}</span>
              {t(cat.labelKey, cat.fallback)}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

const FEATURED_TRIPS = [
  { img: `${process.env.PUBLIC_URL}/photo3.webp`, titleKey: "TRip thailand", countryKey: "Thailand", durationKey: "duration3Days", price: "290", rating: "4.9", reviews: "842", tagKey: "topRated" },
  { img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=85&fit=crop", titleKey: "Trip UAE", countryKey: "country UAE", durationKey: "duration5Days", price: "520", rating: "4.8", reviews: "1,204", tagKey: "bestSeller" },
  { img: `${process.env.PUBLIC_URL}/photo2.webp`, titleKey: "Trip Malaysia", countryKey: "Malaysia", durationKey: "duration4Days", price: "410", rating: "4.9", reviews: "673", tagKey: "editorsPick" },
  { img: `${process.env.PUBLIC_URL}/photo1.jpg`, titleKey: "Trip Oman", countryKey: "Oman", durationKey: "duration7Days", price: "1,850", rating: "5.0", reviews: "388", tagKey: "luxury" },
];

const WHY_US = [
  { icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg>), titleKey: "expertGuidesTitle", textKey: "expertGuidesText" },
  { icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>), titleKey: "endToEndTitle", textKey: "endToEndText" },
  { icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>), titleKey: "guaranteedApprovalsTitle", textKey: "guaranteedApprovalsText" },
  { icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>), titleKey: "support247Title", textKey: "support247Text" },
];

const HOW_STEPS = [
  { num: "01", titleKey: "step1Title", textKey: "step1Text" },
  { num: "02", titleKey: "step2Title", textKey: "step2Text" },
  { num: "03", titleKey: "step3Title", textKey: "step3Text" },
  { num: "04", titleKey: "step4Title", textKey: "step4Text" },
];

const VISA_PLANS = [
  { days: "10 Days", typeKey: "singleEntry", price: "99", processingKey: "processing2_3Days", useKey: "use90Days", stayKey: "stay10Days", feat: false },
  { days: "30 Days", typeKey: "singleEntry", price: "149", processingKey: "processing3_5Days", useKey: "use90Days", stayKey: "stay30Days", feat: true },
  { days: "60 Days", typeKey: "multipleEntry", price: "199", processingKey: "processing5_7Days", useKey: "use90Days", stayKey: "stay60Days", feat: false },
];



const testimonialSettings = {
  dots: true,
  arrows: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 2000,
  pauseOnHover: true, 
  pauseOnFocus: true,  
  adaptiveHeight: true,
  cssEase: "ease-in-out", 
};


interface ActivityPhoto {
  id: number;
  url: string;
}

interface ActivityItem {
  id: number;
  title?: string;
  name?: string;
  description?: string;
  price?: number | string;
  duration?: string;
  location?: string;
  city?: string;
  rating?: number;
  reviews_count?: number;
  photos: ActivityPhoto[];
}

function useActivities() {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");

  const fetchActivities = useCallback(() => {
    setStatus("loading");
    fetch(`${API_BASE_URL}/activities-list`)
      .then((res) => {
        if (!res.ok) throw new Error("Request failed");
        return res.json();
      })
      .then((json) => {
        setActivities(json.data || []);
        setStatus("success");
      })
      .catch(() => setStatus("error"));
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  return { activities, status, retry: fetchActivities };
}

function ActivityCardSkeleton() {
  return <div className="activity-card activity-card--skeleton" />;
}

function ActivityCard({ activity }: { activity: ActivityItem }) {
  const { t } = useTranslation();
  const title = activity.title || activity.name || "Untitled Activity";
  const place = activity.location || activity.city;
  const image = activity.photos?.[0]?.url;

  return (
    <a href={`/activities/${activity.id}`} className="activity-card">
      {image ? (
        <img src={image} alt={title} className="activity-card__img" loading="lazy" />
      ) : (
        <div className="activity-card__img activity-card__img--placeholder">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" />
          </svg>
        </div>
      )}

      <div className="activity-card__overlay" />

      {activity.rating && (
        <div className="activity-card__rating">
          <span className="activity-card__star">★</span>
          {Number(activity.rating).toFixed(1)}
        </div>
      )}

      <div className="activity-card__body">
        {place && <div className="activity-card__place">{place}</div>}
        <h3 className="activity-card__title">{title}</h3>
        <div className="activity-card__footer">
  <div className="activity-card__price-block">
    <span className="activity-card__price-label">{t("from", "From")}</span>
    <span className="activity-card__price">OMR {activity.price}</span>
  </div>
  
  <a href={`/activities/${activity.id}`}
    className="activity-card__book-btn"
    onClick={(e) => e.stopPropagation()}
  >
    {t("bookNow", "Book Now")}
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
  </a>
</div>
      </div>

      <span className="activity-card__cta">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
        </svg>
      </span>
    </a>
  );
}

function ActivitiesSection() {
  const { t } = useTranslation();
  const { activities, status, retry } = useActivities();

  return (
    <>
      <style>{`
        .activities-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 22px;
        }
        @media (max-width: 1100px) { .activities-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 560px) { .activities-grid { grid-template-columns: 1fr; } }

        .activity-card {
          position: relative;
          display: block;
          border-radius: var(--radius);
          overflow: hidden;
          aspect-ratio: 3 / 4;
          text-decoration: none;
          background: var(--mist);
          box-shadow: var(--shadow-sm);
          transition: transform .35s cubic-bezier(.22,.68,0,1.1), box-shadow .35s;
        }
        .activity-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
        .activity-card__img {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          object-fit: cover;
          transition: transform .6s ease;
        }
        .activity-card:hover .activity-card__img { transform: scale(1.08); }
        .activity-card__img--placeholder {
          display: flex; align-items: center; justify-content: center;
          color: var(--silver);
          background: linear-gradient(135deg, var(--mist), #e3ded2);
        }
        .activity-card__overlay {
          position: absolute; inset: 0;
          background: linear-gradient(0deg, rgba(15,25,30,.92) 0%, rgba(15,25,30,.35) 45%, rgba(15,25,30,0) 70%);
        }
        .activity-card__rating {
          position: absolute; top: 14px; left: 14px;
          display: inline-flex; align-items: center; gap: 4px;
          background: rgba(255,255,255,.92);
          color: var(--ink);
          font-size: 12px; font-weight: 700;
          padding: 5px 10px;
          border-radius: 999px;
        }
        .activity-card__star { color: var(--gold-d); }
        .activity-card__body {
          position: absolute; left: 0; right: 0; bottom: 0;
          padding: 20px;
          color: #fff;
        }
        .activity-card__place {
          font-size: 10px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase;
          color: var(--gold); margin-bottom: 6px;
        }
        .activity-card__title {
          font-size: 1.08rem; font-weight: 500; line-height: 1.3;
          margin-bottom: 12px;
          display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
        }
    .activity-card__footer {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding-top: 14px;
  border-top: 1px solid rgba(255,255,255,.18);
}
.activity-card__price-block { display: flex; flex-direction: column; line-height: 1.2; }
.activity-card__price-label { font-size: 10px; font-weight: 400; color: rgba(255,255,255,.6); }
.activity-card__price { font-size: 15px; font-weight: 700; color: #fff; }
.activity-card__book-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--gold);
  color: var(--teal-d);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .3px;
  padding: 9px 14px;
  border-radius: var(--radius-sm);
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background .2s, transform .2s;
}
.activity-card__book-btn:hover {
  background: #e2ce86;
  color: var(--teal-d);
  transform: translateY(-1px);
}
        .activity-card__cta {
          position: absolute; top: 14px; right: 14px;
          width: 32px; height: 32px;
          border-radius: 50%;
          background: rgba(255,255,255,.15);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          color: #fff;
          opacity: 0; transform: translateY(-6px);
          transition: opacity .25s, transform .25s, background .25s;
        }
        .activity-card:hover .activity-card__cta { opacity: 1; transform: translateY(0); }
        .activity-card:hover .activity-card__cta:hover { background: var(--gold); color: var(--teal-d); }

        .activity-card--skeleton {
          background: linear-gradient(90deg, var(--mist) 25%, #efece3 37%, var(--mist) 63%);
          background-size: 400% 100%;
          animation: activitySkeleton 1.4s ease infinite;
        }
        @keyframes activitySkeleton {
          0% { background-position: 100% 50%; }
          100% { background-position: 0 50%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .activity-card--skeleton { animation: none; }
        }

        .activities-error {
          grid-column: 1 / -1;
          text-align: center;
          padding: 48px 20px;
          color: var(--silver);
        }
        .activities-retry {
          margin-top: 14px;
          display: inline-flex;
          border: 1px solid var(--teal);
          color: var(--teal);
          background: transparent;
          border-radius: var(--radius-sm);
          padding: 10px 20px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
        }
        .activities-empty {
          grid-column: 1 / -1;
          text-align: center;
          padding: 48px 20px;
          color: var(--silver);
        }
      `}</style>

      <section className="section-pad bg-cream-section" data-aos="fade-up">
        <div className="container">
          <div className="row justify-between align-items-end" style={{ marginBottom: 44 }}>
                <div className="col-auto">
  <span className="eyebrow">SILVERJARZ EXPERIENCES</span>
  <h2 className="section-title">
    Unforgettable <em>Oman</em> Activities
  </h2>
  <p className="section-sub">
    Hand-picked experiences across Oman — from desert camps under the stars to dhow cruises along the fjords of Musandam.
  </p>
</div>
            <div className="col-auto">
              <a href="/services" className="view-all-link">
                {t("viewAll")}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>
          </div>

          <div className="activities-grid">
            {status === "loading" &&
              Array.from({ length: 4 }).map((_, i) => <ActivityCardSkeleton key={i} />)}

            {status === "error" && (
              <div className="activities-error">
                <p>{t("activitiesLoadError", "We couldn't load activities right now.")}</p>
                <button className="activities-retry" onClick={retry}>
                  {t("tryAgain", "Try again")}
                </button>
              </div>
            )}

            {status === "success" && activities.length === 0 && (
              <div className="activities-empty">{t("noActivitiesYet", "No activities available yet.")}</div>
            )}

            {status === "success" &&
              activities.slice(0, 8).map((activity) => (
                <ActivityCard key={activity.id} activity={activity} />
              ))}
          </div>
        </div>
      </section>
    </>
  );
}

const VISA_DESTINATIONS = [
  {
    code: "GCC",
    country: "GCC Countries",
    tagline: "Six nations, one gateway",
    img: "https://images.unsplash.com/photo-1552329115-e38b0f88f3ec?q=80&w=1000&auto=format&fit=crop",
    processing: "3–5 business days",
    priceFrom: "45",
    popular: true,
  },
  {
    code: "UAE",
    country: "United Arab Emirates",
    tagline: "Dubai & Abu Dhabi tourist visa",
    img: "https://images.unsplash.com/photo-1748373448914-1d7f882700e2?q=80&w=1000&auto=format&fit=crop",
    processing: "2–3 business days",
    priceFrom: "35",
    popular: true,
  },
  {
    code: "OMN",
    country: "Sultanate of Oman",
    tagline: "eVisa & tourist entry",
    img: "https://images.unsplash.com/photo-1599743777555-e362a2feab39?q=80&w=1000&auto=format&fit=crop",
    processing: "1–2 business days",
    priceFrom: "25",
    popular: false,
  },
  {
    code: "QAT",
    country: "Qatar",
    tagline: "Doha tourist & transit visa",
    img: "https://images.unsplash.com/photo-1596986343464-332d54fa5702?q=80&w=1000&auto=format&fit=crop",
    processing: "2–3 business days",
    priceFrom: "40",
    popular: false,
  },
  {
    code: "SCH",
    country: "Schengen Area",
    tagline: "27 European countries",
    img: "https://images.unsplash.com/photo-1694716021376-d44da3f54e8a?q=80&w=1000&auto=format&fit=crop",
    processing: "10–15 business days",
    priceFrom: "120",
    popular: false,
  },
  {
    code: "KSA",
    country: "Kingdom of Saudi Arabia",
    tagline: "Tourist & Umrah visa",
    img: "https://images.unsplash.com/photo-1663900108404-a05e8bf82cda?q=80&w=1000&auto=format&fit=crop",
    processing: "3–5 business days",
    priceFrom: "55",
    popular: false,
  },
  {
    code: "TUR",
    country: "Türkiye",
    tagline: "Istanbul & Cappadocia eVisa",
    img: "https://images.unsplash.com/photo-1752926269883-78794047bac5?q=80&w=1000&auto=format&fit=crop",
    processing: "2–4 business days",
    priceFrom: "40",
    popular: false,
  },
  {
    code: "IND",
    country: "India",
    tagline: "Tourist eVisa",
    img: "https://images.unsplash.com/photo-1523980487775-101d70aae2a9?q=80&w=1000&auto=format&fit=crop",
    processing: "3–5 business days",
    priceFrom: "50",
    popular: false,
  },
  {
    code: "ARM",
    country: "Armenia",
    tagline: "Visa-free & eVisa entry",
    img: "https://images.unsplash.com/photo-1688280506107-8fa36172ae4f?q=80&w=1000&auto=format&fit=crop",
    processing: "1–3 business days",
    priceFrom: "30",
    popular: false,
  },
  {
    code: "BHR",
    country: "Kingdom of Bahrain",
    tagline: "Manama tourist eVisa",
    img: "https://images.unsplash.com/photo-1748066768504-99532da7d1e9?q=80&w=1000&auto=format&fit=crop",
    processing: "1–2 business days",
    priceFrom: "30",
    popular: false,
  },
];

function VisaDestinationsSection() {
  const loop = [...VISA_DESTINATIONS, ...VISA_DESTINATIONS];

  return (
    <>
      <style>{`
        .visa-dest-section {
          position: relative;
          background: var(--teal-d);
          padding: 84px 0;
          overflow: hidden;
        }
        @media (max-width: 991px) { .visa-dest-section { padding: 64px 0; } }

        .visa-dest-viewport {
          margin-top: 46px;
          overflow: hidden;
        }
        .visa-dest-track {
          display: flex;
          width: max-content;
          gap: 18px;
          animation: visaDestScroll 55s linear infinite;
        }
        .visa-dest-viewport:hover .visa-dest-track {
          animation-play-state: paused;
        }
        @keyframes visaDestScroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .visa-dest-track { animation: none; }
        }

        .visa-dest-card {
          position: relative;
          display: block;
          flex-shrink: 0;
          width: 250px;
          aspect-ratio: 3 / 4.3;
          border-radius: var(--radius);
          overflow: hidden;
          text-decoration: none;
          box-shadow: 0 16px 34px rgba(0,0,0,.28);
          transition: transform .4s cubic-bezier(.22,.68,0,1.1), box-shadow .4s;
        }
        @media (max-width: 700px) { .visa-dest-card { width: 210px; } }
        .visa-dest-card:hover { transform: translateY(-7px); box-shadow: 0 24px 48px rgba(0,0,0,.36); }

        .visa-dest-card__img {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          object-fit: cover;
          filter: saturate(.92) brightness(.86);
          transition: transform .7s ease, filter .4s ease;
        }
        .visa-dest-card:hover .visa-dest-card__img { transform: scale(1.08); filter: saturate(1) brightness(.78); }

        .visa-dest-card__overlay {
          position: absolute; inset: 0;
          background: linear-gradient(180deg, rgba(14,25,30,.15) 0%, rgba(14,25,30,.35) 55%, rgba(14,25,30,.95) 100%);
        }

        .visa-dest-card__badge {
          position: absolute; top: 14px; right: 14px;
          width: 36px; height: 36px;
          border-radius: 50%;
          border: 1px solid rgba(211,190,118,.5);
          background: rgba(14,25,30,.4);
          backdrop-filter: blur(3px);
          display: flex; align-items: center; justify-content: center;
          font-family: 'Playfair Display', serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: .5px;
          color: var(--gold);
        }

        .visa-dest-card__popular {
          position: absolute; top: 14px; left: 14px;
          background: var(--gold);
          color: var(--teal-d);
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
          padding: 5px 10px;
          border-radius: 999px;
        }

        .visa-dest-card__body {
          position: absolute; left: 0; right: 0; bottom: 0;
          padding: 18px 16px;
        }
        .visa-dest-card__country {
          font-family: 'Playfair Display', serif;
          font-size: 1.08rem;
          font-weight: 600;
          color: #fff;
          line-height: 1.22;
          margin-bottom: 4px;
        }
        .visa-dest-card__rule {
          width: 24px; height: 2px;
          background: var(--gold);
          border-radius: 2px;
          margin-bottom: 9px;
        }
        .visa-dest-card__tagline {
          font-size: 11px;
          color: rgba(255,255,255,.62);
          font-weight: 300;
          margin-bottom: 14px;
          line-height: 1.4;
        }
        .visa-dest-card__meta {
          display: flex; align-items: center; justify-content: space-between;
          padding-top: 12px;
          border-top: 1px solid rgba(255,255,255,.15);
        }
        .visa-dest-card__price-label { font-size: 9px; letter-spacing: .5px; text-transform: uppercase; color: rgba(255,255,255,.5); }
        .visa-dest-card__price { font-size: 14px; font-weight: 700; color: #fff; }
        .visa-dest-card__price sup { font-size: .6em; font-weight: 500; color: rgba(255,255,255,.6); }

        .visa-dest-card__arrow {
          width: 28px; height: 28px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,.3);
          display: flex; align-items: center; justify-content: center;
          color: #fff;
          transition: background .25s, border-color .25s, transform .25s;
        }
        .visa-dest-card:hover .visa-dest-card__arrow {
          background: var(--gold);
          border-color: var(--gold);
          color: var(--teal-d);
          transform: translateX(2px);
        }

        .visa-dest-processing {
          font-size: 10px;
          color: rgba(255,255,255,.55);
          margin-top: 6px;
        }
      `}</style>

      <section className="visa-dest-section">
        <div className="container">
          <div className="row justify-between align-items-end">
            <div className="col-auto">
              <span className="eyebrow light">GLOBAL VISA SERVICES</span>
              <h2 className="section-title" style={{ color: "#fff" }}>
                Your Passport to <em style={{ color: "var(--gold)" }}>Anywhere</em>
              </h2>
              <p className="section-sub" style={{ color: "rgba(255,255,255,.55)" }}>
                Fast, reliable visa processing across the Gulf, Europe, and beyond — handled end-to-end by our team in Muscat.
              </p>
            </div>
            <div className="col-auto">
              <a href="/visa" className="view-all-link" style={{ color: "#fff", borderColor: "var(--gold)" }}>
                View all visas
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="visa-dest-viewport">
          <div className="visa-dest-track">
            {loop.map((v, i) => (
              <a href={`/visa/${v.code.toLowerCase()}`} className="visa-dest-card" key={`${v.code}-${i}`}>
                <img src={v.img} alt={v.country} className="visa-dest-card__img" loading="lazy" />
                <div className="visa-dest-card__overlay" />

                {v.popular && <span className="visa-dest-card__popular">Popular</span>}
                <span className="visa-dest-card__badge">{v.code}</span>

                <div className="visa-dest-card__body">
                  <div className="visa-dest-card__country">{v.country}</div>
                  <div className="visa-dest-card__rule" />
                  <p className="visa-dest-card__tagline">{v.tagline}</p>

                  <div className="visa-dest-card__meta">
                    <div>
                      <div className="visa-dest-card__price-label">From</div>
                      <div className="visa-dest-card__price"><sup>OMR </sup>{v.priceFrom}</div>
                    </div>
                    <span className="visa-dest-card__arrow">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                      </svg>
                    </span>
                  </div>
                  <div className="visa-dest-processing">{v.processing}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function TestimonialsSection() {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const avgRating = (
    testimonial1.reduce((sum, item) => sum + (item.rating ?? 5), 0) / testimonial1.length
  ).toFixed(1);
  const reviewCount = testimonial1.length;

  const goTo = useCallback((i: number) => {
    setIdx((i + testimonial1.length) % testimonial1.length);
  }, []);

  const next = useCallback(() => goTo(idx + 1), [idx, goTo]);

  useEffect(() => {
    timerRef.current = setInterval(next, 6000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [next]);

  const restartTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(next, 6000);
  };

  const current = testimonial1[idx];

  return (
    <>
      <style>{`
        .testi-section {
          position: relative;
          background: var(--teal-d);
          padding: 20px 32px;
          overflow: hidden;
          border-radius: 28px;
          margin-left:20px;
          margin-right:20px;
        }
        @media (max-width: 700px) {
          .testi-section { padding: 32px 18px; }
        }

        .testi-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: 48px;
          align-items: center;
          max-width: 1100px;
          margin: 0 auto;
        }
        @media (max-width: 900px) {
          .testi-grid { grid-template-columns: 1fr; gap: 32px; }
        }

        .testi-rating-block { display: flex; align-items: baseline; gap: 14px; margin-bottom: 6px; }
        .testi-rating-num { font-family: 'Playfair Display', serif; font-size: 2.6rem; font-weight: 600; color: #fff; line-height: 1; }
        .testi-rating-stars-row { display: flex; gap: 3px; }
        .testi-rating-stars-row span { color: var(--gold); font-size: 14px; }
        .testi-rating-count { font-size: 11px; color: rgba(255,255,255,.55); }
        .testi-google-badge { display: inline-flex; align-items: center; gap: 6px; margin: 10px 0 20px; font-size: 11px; color: rgba(255,255,255,.6); }

        .testi-stats-list { display: flex; flex-direction: column; gap: 12px; }
        .testi-stat-row { display: flex; align-items: baseline; gap: 10px; padding-bottom: 10px; border-bottom: 1px solid rgba(255,255,255,.1); }
        .testi-stat-row:last-child { border-bottom: none; padding-bottom: 0; }
        .testi-stat-num { font-family: 'Playfair Display', serif; font-size: 1.15rem; font-weight: 600; color: var(--gold); min-width: 60px; }
        .testi-stat-label { font-size: 11.5px; color: rgba(255,255,255,.6); line-height: 1.3; }

        .testi-card {
          position: relative;
          background: rgba(255,255,255,.04);
          border: 1px solid rgba(211,190,118,.18);
          border-radius: var(--radius);
          padding: 22px 26px 18px;
        }
        @media (max-width: 500px) {
          .testi-card { padding: 18px 18px 16px; }
        }
        .testi-card__mark { font-family: 'Playfair Display', serif; font-size: 2.4rem; color: var(--gold); opacity: .5; line-height: 1; margin-bottom: 0; display: block; }
        .testi-card__stars { display: flex; gap: 3px; margin-bottom: 12px; }
        .testi-card__stars span { color: var(--gold); font-size: 13px; }
        .testi-card__text { font-size: 15px; font-weight: 300; font-style: italic; line-height: 1.6; color: rgba(255,255,255,.9); margin-bottom: 18px; }
        .testi-card__footer { display: flex; align-items: center; padding-top: 14px; border-top: 1px solid rgba(255,255,255,.1); }
        .testi-card__person { display: flex; align-items: center; gap: 12px; }
        .testi-card__avatar { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; border: 2px solid rgba(211,190,118,.4); }
        .testi-card__name { font-size: 13px; font-weight: 600; color: #fff; margin-bottom: 1px; }
        .testi-card__role { font-size: 9.5px; font-weight: 600; letter-spacing: .6px; text-transform: uppercase; color: var(--gold); }

        .testi-nav { display: flex; align-items: center; gap: 12px; justify-content: flex-end; margin-top: 14px; }
        .testi-dots { display: flex; gap: 6px; margin-right: auto; }
        .testi-dot { width: 7px; height: 7px; border-radius: 50%; border: none; background: rgba(255,255,255,.25); cursor: pointer; padding: 0; transition: background .2s, width .2s; }
        .testi-dot.on { background: var(--gold); width: 18px; border-radius: 4px; }
        .testi-nav-btn { width: 32px; height: 32px; border-radius: 50%; border: 1px solid rgba(255,255,255,.25); background: transparent; color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background .2s, border-color .2s, color .2s; flex-shrink: 0; }
        .testi-nav-btn:hover { background: var(--gold); border-color: var(--gold); color: var(--teal-d); }
      `}</style>

      <section className="testi-section" data-aos="fade-up">
        <div className="testi-grid">

          <div>
            <span className="eyebrow light">SOCIAL PROOF</span>
            <h2 className="section-title" style={{ color: "#fff", marginBottom: 14, fontSize: 19 }}>
              What Our <em style={{ color: "var(--gold)" }}>Clients Say</em>
            </h2>

            <div className="testi-rating-block">
              <div className="testi-rating-num">{avgRating}</div>
              <div>
                <div className="testi-rating-stars-row">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ opacity: i < Math.round(Number(avgRating)) ? 1 : 0.25 }}>★</span>
                  ))}
                </div>
                <div className="testi-rating-count">Based on {reviewCount} client reviews</div>
              </div>
            </div>

            <div className="testi-google-badge">
              <svg width="13" height="13" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Real reviews from Google
            </div>

            <div className="testi-stats-list">
              {TRUST_STATS.map((s, i) => (
                <div className="testi-stat-row" key={i}>
                  <div className="testi-stat-num">{s.value}</div>
                  <div className="testi-stat-label">{s.fallback}</div>
                </div>
              ))}
            </div>
          </div>

          <div onMouseEnter={() => { if (timerRef.current) clearInterval(timerRef.current); }} onMouseLeave={restartTimer}>
            <div className="testi-card">
              <span className="testi-card__mark">"</span>

              <div className="testi-card__stars">
                {[...Array(5)].map((_, i) => (
                  <span key={i} style={{ opacity: i < (current.rating ?? 5) ? 1 : 0.25 }}>★</span>
                ))}
              </div>

              <p className="testi-card__text">{current.text}</p>

              <div className="testi-card__footer">
                <div className="testi-card__person">
                  <img className="testi-card__avatar" src={current.avatar} alt={current.name} />
                  <div>
                    <div className="testi-card__name">{current.name}</div>
                    <div className="testi-card__role">{current.designation}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="testi-nav">
              <div className="testi-dots">
                {testimonial1.map((_, i) => (
                  <button
                    key={i}
                    className={`testi-dot${i === idx ? " on" : ""}`}
                    onClick={() => { goTo(i); restartTimer(); }}
                    aria-label={`Testimonial ${i + 1}`}
                  />
                ))}
              </div>
              <button className="testi-nav-btn" onClick={() => { goTo(idx - 1); restartTimer(); }} aria-label="Previous">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
              </button>
              <button className="testi-nav-btn" onClick={() => { goTo(idx + 1); restartTimer(); }} aria-label="Next">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </button>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}

const FLIGHT_GUARANTEES = [
  "Best price",
  "Free cancellation",
  "Secure payment",
  "24/7 support",
  "500+ airlines",
  "E-ticket",
];

const DUMMY_TICKET_CHECKLIST = [
  "Full name exactly as on your passport",
  "Passport number and nationality",
  "Preferred travel dates",
  "A valid email to receive your PDF",
];

function DocumentServicesSection() {
  return (
    <>
      <style>{`
        .doc-section { padding: 84px 0; }
        @media (max-width: 991px) { .doc-section { padding: 64px 0; } }

        .doc-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 28px;
          margin-top: 44px;
        }
        @media (max-width: 900px) { .doc-grid { grid-template-columns: 1fr; } }

        .doc-card {
          background: #fff;
          border-radius: var(--radius);
          overflow: hidden;
          box-shadow: var(--shadow-sm);
          border: 1px solid rgba(41,75,90,.08);
          transition: transform .3s, box-shadow .3s;
          display: flex;
          flex-direction: column;
        }
        .doc-card:hover { transform: translateY(-5px); box-shadow: var(--shadow-md); }

        .doc-card__band {
  position: relative;
  padding: 30px 32px;
  background: linear-gradient(135deg, var(--teal) 0%, var(--teal-d) 100%);
  overflow: hidden;
}
  
        .doc-card__band::after {
          content: "";
          position: absolute;
          top: -60px; right: -60px;
          width: 160px; height: 160px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(211,190,118,.16) 0%, transparent 70%);
        }
        .doc-card__icon {
          width: 46px; height: 46px;
          border-radius: 12px;
          background: rgba(211,190,118,.15);
          border: 1px solid rgba(211,190,118,.4);
          display: flex; align-items: center; justify-content: center;
          color: var(--gold);
          margin-bottom: 16px;
        }
        .doc-card__eyebrow {
          font-size: 10.5px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase;
          color: var(--gold); margin-bottom: 6px;
        }
        .doc-card__title {
          font-family: 'Playfair Display', serif;
          font-size: 1.45rem;
          font-weight: 600;
          color: #fff;
          line-height: 1.25;
          position: relative;
        }

        .doc-card__body { padding: 28px 32px 32px; flex: 1; display: flex; flex-direction: column; }
        .doc-card__desc { font-size: 14px; color: var(--silver); font-weight: 300; line-height: 1.7; margin-bottom: 22px; }

        .doc-check-list { display: flex; flex-direction: column; gap: 13px; margin-bottom: 26px; flex: 1; }
        .doc-check-item { display: flex; align-items: flex-start; gap: 11px; font-size: 13.5px; color: var(--ink); line-height: 1.4; }
        .doc-check-icon {
          width: 19px; height: 19px;
          border-radius: 50%;
          background: rgba(211,190,118,.15);
          display: flex; align-items: center; justify-content: center;
          font-size: 9px; color: var(--gold-d);
          flex-shrink: 0; margin-top: 1px;
        }

        .doc-card__cta {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          background: var(--teal);
          color: #fff;
          border: none;
          border-radius: var(--radius-sm);
          font-size: 12px; font-weight: 700; letter-spacing: .3px;
          padding: 14px 22px;
          text-decoration: none;
          transition: background .2s, transform .2s;
        }
        .doc-card__cta:hover { background: var(--teal-d); transform: translateY(-1px); color: #fff; }
        .doc-card__cta.gold { background: var(--gold); color: var(--teal-d); }
        .doc-card__cta.gold:hover { background: var(--gold-d); color: var(--teal-d); }
      `}</style>

      <section className="doc-section bg-cream-section" data-aos="fade-up">
        <div className="container">
          <div className="row justify-between align-items-end">
            <div className="col-auto">
              <span className="eyebrow">TRAVEL DOCUMENTS</span>
              <h2 className="section-title">
                Flights & <em>Booking Documents</em>, Sorted
              </h2>
              <p className="section-sub">
                Whether you're booking a real trip or need a confirmed reservation for your visa file, our team handles it end-to-end.
              </p>
            </div>
          </div>

          <div className="doc-grid">

            {/* Card 1 — Flight ticket request */}
            <div className="doc-card">
              <div className="doc-card__band">
                <div className="doc-card__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2.5 1.8V22l4-1 4 1v-1.2L13 19v-5.5z" />
                  </svg>
                </div>
                <div className="doc-card__eyebrow">Travel Booking</div>
                <div className="doc-card__title">Request Your Flight Ticket</div>
              </div>
              <div className="doc-card__body">
                <p className="doc-card__desc">
                  Fill in your travel details and passengers, and our team will search across airlines and confirm your booking within 24 hours.
                </p>
                <div className="doc-check-list">
                  {FLIGHT_GUARANTEES.map((g, i) => (
                    <div className="doc-check-item" key={i}>
                      <span className="doc-check-icon">✓</span>
                      {g}
                    </div>
                  ))}
                </div>
                <a href="/travel/request-ticket" className="doc-card__cta">
                  Request a Flight Ticket
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Card 2 — Dummy ticket for visa */}
            <div className="doc-card">
              <div className="doc-card__band">
                <div className="doc-card__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <rect x="4" y="3" width="16" height="18" rx="2" />
                    <line x1="8" y1="8" x2="16" y2="8" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="8" y1="16" x2="12" y2="16" />
                  </svg>
                </div>
                <div className="doc-card__eyebrow">Reserve Yours</div>
                <div className="doc-card__title">Request a Dummy Ticket</div>
              </div>
              <div className="doc-card__body">
                <p className="doc-card__desc">
                  Need a flight reservation for your visa application? Have these ready and we'll send your PDF fast — no mismatches, no delays.
                </p>
                <div className="doc-check-list">
                  {DUMMY_TICKET_CHECKLIST.map((c, i) => (
                    <div className="doc-check-item" key={i}>
                      <span className="doc-check-icon">✓</span>
                      {c}
                    </div>
                  ))}
                </div>
                <a href="/travel/dummy-ticket" className="doc-card__cta gold">
                  Request Dummy Ticket
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                  </svg>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

// ============================================================
// HOME PAGE
// ============================================================

function Home() {
  const { t } = useTranslation();
  const [dates, setDates] = useState([
    new DateObject().setDay(5),
    new DateObject().setDay(14).add(1, "month"),
  ]);

  useSeo({
    title: "SilverJarz | Tourist Visas, Tours & Travel Services in Oman",
    // Phase 3 correction: the Phase 2 version of this description mentioned
    // "hotel bookings", but no Hotels route or feature exists anywhere in
    // the project — removed to avoid claiming a service that isn't offered.
    description: "Tourist visas, Gulf & Schengen visas, flight tickets, travel insurance and car rental, plus curated tour packages — all from Oman with SilverJarz.",
    canonical: typeof window !== "undefined" ? window.location.origin + "/" : undefined,
    image: typeof window !== "undefined" ? window.location.origin + "/img/home_cover.jpg" : undefined,
    jsonLd: organizationJsonLd(),
  });
  
  useEffect(() => {
    AOS.init({ duration: 750, easing: "ease-out-quad", once: true });
  }, []);

  const bento = destinations10.slice(0, 5);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
          --radius: 10px;
          --radius-sm: 5px;
          --shadow-sm: 0 6px 20px rgba(26, 49, 61, .07);
          --shadow-md: 0 20px 48px rgba(26, 49, 61, .12);
          --shadow-lg: 0 28px 64px rgba(26, 49, 61, .18);
        }
        * { box-sizing: border-box; }
        body { font-family: 'DM Sans', sans-serif; background: var(--cream); -webkit-font-smoothing: antialiased; }
        h1, h2, h3, h4 { font-family: 'Playfair Display', serif; }
        a:focus-visible, button:focus-visible, input:focus-visible { outline: 2px solid var(--gold-d); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; } }

        .eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-d); }
        .eyebrow::before { content: ''; width: 22px; height: 2px; background: var(--gold); border-radius: 2px; }
        .eyebrow.light { color: var(--gold-l); }
        .eyebrow.light::before { background: var(--gold-l); opacity: .6; }

        .section-title { font-size: 22px; font-weight: 800; color: var(--ink); line-height: 1.18; letter-spacing: -.01em; margin: 10px 0 12px; }
        .section-title em { font-style: italic; color: var(--teal-l); }
        .section-sub { font-size: 15px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 480px; }
        .section-pad { padding: 30px 0; }
        @media (max-width: 991px) { .section-pad { padding: 72px 0; } }
        @media (max-width: 576px) { .section-pad { padding: 52px 0; } }
        .bg-cream-section { background: var(--cream); }
        .bg-white-section { background: var(--white, #fff); }
        .bg-dark-section { background: var(--teal-d); }

        .view-all-link { display: inline-flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 600; letter-spacing: .4px; color: var(--teal); text-decoration: none; border-bottom: 1px solid var(--gold); padding-bottom: 4px; transition: color .2s, border-color .2s, gap .2s; }
        .view-all-link:hover { color: var(--gold-d); border-color: var(--gold-d); gap: 11px; }

        /* SEARCH BAR — restored floating card under the hero */
        .search-float-wrap { position: relative; z-index: 20; margin-top: -42px; padding-bottom: 56px; }
        .search-bar {
          background: #fff; border-radius: var(--radius);
          box-shadow: var(--shadow-lg), 0 0 0 1px rgba(211,190,118,.18);
          display: grid; grid-template-columns: 1fr 1fr 170px 58px; overflow: hidden;
        }
        @media (max-width: 768px) { .search-bar { grid-template-columns: 1fr; } }
        .sf { padding: 20px 24px; border-right: 1px solid var(--mist); display: flex; flex-direction: column; gap: 7px; transition: background .18s; cursor: pointer; }
        .sf:hover { background: #faf8f4; }
        .sf-lbl { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--teal); }
        .sf-row { display: flex; align-items: center; gap: 10px; }
        .sf-ico { color: var(--gold); font-size: 17px; flex-shrink: 0; }
        .sf-input, .sf-sel, .custom_input-picker {
          font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 500;
          color: var(--ink); background: none; border: none; outline: none; width: 100%;
        }
        .sf-sel { cursor: pointer; appearance: none; }
        .sf-input::placeholder, .custom_input-picker::placeholder { color: #b5afa6; font-weight: 300; }
        .custom_container-picker { width: 100%; }
        .search-btn { display: flex; align-items: center; justify-content: center; min-height: 100%; background: var(--teal); color: var(--gold); border: none; cursor: pointer; font-size: 20px; transition: background .2s; }
        .search-btn:hover { background: var(--teal-d); }

        .stats-strip { background: var(--teal); padding: 44px 0; }
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); }
        @media (max-width: 700px) { .stats-grid { grid-template-columns: repeat(2, 1fr); row-gap: 28px; } }
        .stat-item { text-align: center; padding: 0 16px; border-right: 1px solid rgba(255,255,255,.1); }
        .stat-item:last-child { border-right: none; }
        @media (max-width: 700px) { .stat-item:nth-child(2n) { border-right: none; } }
        .stat-num { font-size: 2.1rem; font-weight: 600; color: var(--gold); line-height: 1; margin-bottom: 6px; display: block; }
        .stat-label { font-size: 11px; color: rgba(255,255,255,.55); letter-spacing: .5px; }

        .bento-grid { display: grid; grid-template-columns: repeat(4, 1fr); grid-template-rows: repeat(2, 200px); gap: 16px; }
        .bento-grid .dest-card:nth-child(1) { grid-column: 1 / 3; grid-row: 1 / 3; }
        .bento-grid .dest-card:nth-child(2) { grid-column: 3 / 4; grid-row: 1 / 2; }
        .bento-grid .dest-card:nth-child(3) { grid-column: 4 / 5; grid-row: 1 / 2; }
        .bento-grid .dest-card:nth-child(4) { grid-column: 3 / 4; grid-row: 2 / 3; }
        .bento-grid .dest-card:nth-child(5) { grid-column: 4 / 5; grid-row: 2 / 3; }
        @media (max-width: 900px) {
          .bento-grid { grid-template-columns: repeat(2, 1fr); grid-template-rows: repeat(3, 180px); }
          .bento-grid .dest-card:nth-child(1) { grid-column: 1 / 3; grid-row: 1 / 2; }
          .bento-grid .dest-card:nth-child(2) { grid-column: 1 / 2; grid-row: 2 / 3; }
          .bento-grid .dest-card:nth-child(3) { grid-column: 2 / 3; grid-row: 2 / 3; }
          .bento-grid .dest-card:nth-child(4) { grid-column: 1 / 2; grid-row: 3 / 4; }
          .bento-grid .dest-card:nth-child(5) { grid-column: 2 / 3; grid-row: 3 / 4; }
        }
        .dest-card { position: relative; display: block; border-radius: var(--radius); overflow: hidden; text-decoration: none; transition: transform .35s cubic-bezier(.22,.68,0,1.1), box-shadow .35s; height: 100%; }
        .dest-card:hover { transform: translateY(-5px); box-shadow: var(--shadow-md); }
        .dest-card img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .6s ease; }
        .dest-card:hover img { transform: scale(1.06); }
        .dest-card-overlay { position: absolute; inset: 0; background: linear-gradient(0deg, rgba(20,40,50,.9) 0%, rgba(20,40,50,.1) 55%, transparent 100%); }
        .dest-card-body { position: absolute; bottom: 0; left: 0; right: 0; padding: 20px; }
        .dest-card-country { font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold); margin-bottom: 5px; }
        .dest-card-city { font-size: 1.15rem; font-weight: 500; color: #fff; line-height: 1.2; margin-bottom: 4px; }
        .dest-card-props { font-size: 11px; color: rgba(255,255,255,.55); }
        .dest-card-tag { position: absolute; top: 16px; left: 0; background: var(--gold); color: var(--ink); font-size: 9px; font-weight: 700; letter-spacing: .8px; padding: 6px 14px; border-radius: 0 4px 4px 0; z-index: 2; }

        .trip-card { background: #fff; border-radius: var(--radius); border: 1px solid rgba(41,75,90,.09); overflow: hidden; transition: transform .35s, box-shadow .35s, border-color .35s; display: flex; flex-direction: column; }
        .trip-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-md); border-color: rgba(211,190,118,.4); }
        .trip-img-wrap { position: relative; overflow: hidden; aspect-ratio: 16/10; }
        .trip-img-wrap img { width: 100%; height: 100%; object-fit: cover; transition: transform .6s ease; }
        .trip-card:hover .trip-img-wrap img { transform: scale(1.07); }
        .trip-tag-pill { position: absolute; top: 12px; left: 12px; background: var(--gold); color: var(--ink); font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 6px 12px; border-radius: var(--radius-sm); }
        .trip-body { padding: 22px 24px 24px; flex: 1; display: flex; flex-direction: column; }
        .trip-country { font-size: 10px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; color: var(--gold-d); margin-bottom: 6px; }
        .trip-title { font-size: 1.15rem; font-weight: 500; color: var(--ink); line-height: 1.3; margin-bottom: 12px; }
        .trip-meta { display: flex; align-items: center; justify-content: space-between; padding-top: 16px; margin-top: auto; border-top: 1px solid rgba(41,75,90,.07); }
        .trip-duration { font-size: 12px; color: var(--silver); display: flex; align-items: center; gap: 5px; }
        .trip-rating { display: flex; align-items: center; gap: 5px; }
        .trip-star { color: var(--gold); font-size: 11px; }
        .trip-price-row { display: flex; align-items: center; justify-content: space-between; margin-top: 18px; }
        .trip-from { font-size: 10px; letter-spacing: .8px; text-transform: uppercase; color: var(--silver); margin-bottom: 2px; }
        .trip-price { font-size: 1.55rem; font-weight: 600; color: var(--teal); }
        .trip-price sup { font-family: 'DM Sans', sans-serif; font-size: .55em; font-weight: 600; color: var(--silver); top: -.6em; }
        .trip-btn { display: inline-flex; align-items: center; gap: 6px; background: var(--teal); color: #fff; border: none; border-radius: var(--radius-sm); font-size: 11px; font-weight: 700; letter-spacing: .3px; padding: 11px 20px; cursor: pointer; text-decoration: none; transition: background .2s, transform .2s; }
        .trip-btn:hover { background: var(--teal-d); transform: translateY(-1px); }

        .route-wrap { position: relative; padding-top: 8px; }
        .route-line { position: absolute; top: 29px; left: 6%; right: 6%; height: 0; border-top: 2px dashed rgba(211,190,118,.6); z-index: 0; }
        .route-plane { position: absolute; top: 16px; left: 48%; color: var(--gold-d); transform: rotate(90deg); z-index: 1; }
        .how-grid { display: grid; grid-template-columns: repeat(4, 1fr); position: relative; z-index: 1; }
        @media (max-width: 768px) { .how-grid { grid-template-columns: repeat(2, 1fr); gap: 40px 20px; } .route-line, .route-plane { display: none; } }
        .how-item { text-align: center; padding: 0 20px; }
        .how-num-ring { width: 58px; height: 58px; border-radius: 50%; border: 1px solid var(--gold); display: flex; align-items: center; justify-content: center; margin: 0 auto 22px; background: #fff; box-shadow: 0 0 0 6px var(--cream); }
        .how-num { font-size: 1.3rem; font-weight: 600; color: var(--teal); }
        .how-title { font-size: 14.5px; font-weight: 600; color: var(--ink); margin-bottom: 9px; }
        .how-text { font-size: 13px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 210px; margin: 0 auto; }

        .why-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
        @media (max-width: 1024px) { .why-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 576px) { .why-grid { grid-template-columns: 1fr; } }
        .why-card { background: #fff; border: 1px solid rgba(41,75,90,.09); border-radius: var(--radius); padding: 30px 26px; transition: transform .3s, box-shadow .3s, border-color .3s; }
        .why-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-sm); border-color: rgba(211,190,118,.35); }
        .why-icon { color: var(--gold-d); margin-bottom: 18px; display: block; }
        .why-title { font-size: 1rem; font-weight: 700; color: var(--teal); margin-bottom: 9px; }
        .why-text { font-size: 13px; color: var(--silver); line-height: 1.7; font-weight: 300; }

        .visa-strip-bg { background: var(--cream); }
        .visa-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 26px; }
        @media (max-width: 991px) { .visa-grid { grid-template-columns: 1fr; max-width: 420px; margin: 0 auto; } }
        .visa-card { border-radius: var(--radius); border: 1px solid rgba(41,75,90,.1); background: #fff; position: relative; transition: transform .3s, box-shadow .3s, border-color .3s; }
        .visa-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); border-color: rgba(211,190,118,.4); }
        .visa-card.feat { border-color: var(--gold); }
        .visa-badge { position: absolute; top: -1px; right: 22px; background: var(--gold); color: var(--teal-d); font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 6px 14px; border-radius: 0 0 5px 5px; }
        .visa-head { padding: 30px 28px 22px; }
        .visa-days { font-size: 2.1rem; font-weight: 600; color: var(--ink); line-height: 1; margin-bottom: 6px; }
        .visa-type { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--gold-d); }
        .visa-price-row { margin-top: 14px; display: flex; align-items: baseline; gap: 6px; }
        .visa-price-num { font-size: 1.7rem; font-weight: 600; color: var(--teal); }
        .visa-price-cur { font-size: 11px; color: var(--silver); letter-spacing: .5px; }
        .visa-perforation { position: relative; height: 0; border-top: 2px dashed var(--mist); margin: 0 28px; }
        .visa-perforation::before, .visa-perforation::after { content: ''; position: absolute; top: -11px; width: 22px; height: 22px; border-radius: 50%; background: var(--cream); }
        .visa-perforation::before { left: -39px; }
        .visa-perforation::after { right: -39px; }
        .visa-body { padding: 22px 28px 26px; }
        .visa-row { display: flex; align-items: flex-start; gap: 12px; padding: 10px 0; border-bottom: 1px solid rgba(41,75,90,.06); font-size: 13px; }
        .visa-row:last-of-type { border-bottom: none; }
        .visa-check { width: 20px; height: 20px; border-radius: 50%; background: rgba(211,190,118,.15); display: flex; align-items: center; justify-content: center; font-size: 9px; color: var(--gold-d); flex-shrink: 0; margin-top: 1px; }
        .visa-label { color: var(--silver); font-weight: 300; line-height: 1.5; }
        .visa-label strong { display: block; color: var(--ink); font-weight: 500; font-size: 12.5px; margin-bottom: 1px; }
        .visa-apply { display: block; width: 100%; margin-top: 20px; background: var(--teal); color: #fff; border: none; border-radius: var(--radius-sm); font-size: 11px; font-weight: 700; letter-spacing: .4px; padding: 15px 20px; cursor: pointer; text-align: center; text-decoration: none; transition: background .2s, transform .2s; }
        .visa-apply:hover { background: var(--teal-d); transform: translateY(-1px); }
        .visa-apply.feat-btn { background: var(--gold); color: var(--teal-d); }
        .visa-apply.feat-btn:hover { background: var(--gold-d); }

        .spotlight-wrap { max-width: 720px; margin: 0 auto; text-align: center; }
        .spotlight-quote { font-size: 3.4rem; color: var(--gold); line-height: 1; opacity: .5; margin-bottom: 6px; }
        .spotlight-text { font-size: 18px; color: rgba(255,255,255,.88); font-weight: 300; line-height: 1.75; font-style: italic; margin-bottom: 28px; }
        .spotlight-stars { display: flex; justify-content: center; gap: 4px; margin-bottom: 18px; }
        .spotlight-stars span { color: var(--gold); font-size: 14px; }
        .spotlight-person { display: flex; align-items: center; justify-content: center; gap: 14px; }
        .spotlight-avatar { width: 52px; height: 52px; border-radius: 50%; object-fit: cover; border: 2px solid rgba(211,190,118,.4); }
        .spotlight-name { font-size: 14px; font-weight: 600; color: #fff; margin-bottom: 2px; }
        .spotlight-role { font-size: 11px; font-weight: 600; color: var(--gold); letter-spacing: 1px; text-transform: uppercase; }
        .spotlight-wrap .slick-dots { bottom: -36px; }
        .spotlight-wrap .slick-dots li button:before { color: rgba(255,255,255,.3) !important; font-size: 8px; }
        .spotlight-wrap .slick-dots li.slick-active button:before { color: var(--gold) !important; }

        .act-card { background: #fff; border: 1px solid rgba(41,75,90,.08); border-radius: var(--radius); overflow: hidden; transition: transform .3s, box-shadow .3s; display: block; text-decoration: none; }
        .act-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-sm); }
        .act-img { width: 100%; aspect-ratio: 4/3; object-fit: cover; transition: transform .55s; }
        .act-card:hover .act-img { transform: scale(1.05); }
        .act-body { padding: 20px 22px; }
        .act-dur { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--gold-d); margin-bottom: 6px; }
        .act-title { font-size: 1.05rem; font-weight: 500; color: var(--ink); margin-bottom: 6px; }
        .act-loc { font-size: 12px; color: var(--silver); margin-bottom: 14px; }
        .act-foot { display: flex; justify-content: space-between; align-items: center; padding-top: 12px; border-top: 1px solid rgba(41,75,90,.07); }
        .act-price strong { color: var(--teal); font-size: 15px; }

        .slick-dots li button:before { color: var(--teal) !important; }
        .slick-dots li.slick-active button:before { color: var(--gold) !important; }
      `}</style>

      <Header />
      <PromoModal />
      <HeroSection />
      <ServiceCategoriesStrip />

      <section className="section-pad bg-white-section" data-aos="fade-up">
        <div className="container">
          <div className="row justify-between align-items-end" style={{ marginBottom: 44 }}>
            <div className="col-auto">
              <span className="eyebrow">{t("exploreTheWorld")}</span>
              <h2 className="section-title" dangerouslySetInnerHTML={{ __html: t("premiumDestinations") }} />
              <p className="section-sub">{t("handpickedEscapes")}</p>
            </div>
            <div className="col-auto">
              <a href="/trips" className="view-all-link">{t("viewAll")} <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></a>
            </div>
          </div>
          <div className="bento-grid">
            {bento.map((item, idx) => (
              <a key={item.id} href="/trips" className="dest-card">
                {idx === 0 && <span className="dest-card-tag">{t("featured")}</span>}
                <img src={item.img} alt={item.location} />
                <div className="dest-card-overlay" />
                <div className="dest-card-body">
                  <div className="dest-card-country">{(item as any).country}</div>
                  <div className="dest-card-city">{item.location}</div>
                  <div className="dest-card-props">{item.numberOfProperties} {t("properties")}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>


      <ActivitiesSection />

      <VisaDestinationsSection />


      <section className="section-pad bg-white-section" data-aos="fade-up">
        <div className="container">
          <div className="text-center" style={{ marginBottom: 60 }}>
            <span className="eyebrow">{t("simpleProcess")}</span>
            <h2 className="section-title" dangerouslySetInnerHTML={{ __html: t("howItWorks") }} />
          </div>
          <div className="route-wrap">
            <div className="route-line" />
            <svg className="route-plane" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2.5 1.8V22l4-1 4 1v-1.2L13 19v-5.5z"/></svg>
            <div className="how-grid">
              {HOW_STEPS.map((s, i) => (
                <div key={i} className="how-item" data-aos="fade-up" data-aos-delay={i * 100}>
                  <div className="how-num-ring"><span className="how-num">{s.num}</span></div>
                  <div className="how-title">{t(s.titleKey)}</div>
                  <p className="how-text">{t(s.textKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-cream-section" data-aos="fade-up">
        <div className="container">
          <div className="row justify-between align-items-end" style={{ marginBottom: 44 }}>
            <div className="col-auto">
              <span className="eyebrow">{t("ourStrengths")}</span>
              <h2 className="section-title" dangerouslySetInnerHTML={{ __html: t("whyChooseUs") }} />
              <p className="section-sub">{t("trustText")}</p>
            </div>
          </div>
          <div className="why-grid">
            {WHY_US.map((w, i) => (
              <div key={i} className="why-card" data-aos="fade-up" data-aos-delay={i * 90}>
                <span className="why-icon">{w.icon}</span>
                <div className="why-title">{t(w.titleKey)}</div>
                <p className="why-text">{t(w.textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>



      <TestimonialsSection />

      <DocumentServicesSection />

      <DefaultFooter />
    </>
  );
}

export default Home;